/**
 * Agent-context file scaffolding written by the CLI.
 *
 * Two layers, two jobs:
 *
 *  1. Repo-root `AGENTS.md` + `CLAUDE.md` — a session BOOTSTRAP: keep the one
 *     skill and the one CLI current, then load the skill. Generic; the same in
 *     every repo. Seeded by `ensureWorkspaceFiles` on init/pull/push.
 *  2. Per-hub `wayai-ws/hubs/<hub>/AGENTS.md` — hub-specific MEMORY: the context
 *     the config files (hub.yaml, agents/) can't capture. A scaffold the agent
 *     fills in. Seeded by `writeHubFolder` (yaml-writer.ts) via `perHubAgentsMd`.
 *
 * Drift: `ROOT_AGENTS_MD` is mirrored (canonically) in
 * `skills/wayai/references/agents-md-template.md`, and a drift-guard test keeps
 * the two byte-identical. At runtime the agent also reconciles a stale on-disk
 * copy against the skill reference (SKILL.md session-start step) — so the seed
 * is a bootstrap, not a permanent frozen artifact (the failure mode that got the
 * original init-writes-AGENTS.md behavior removed in #2966).
 *
 * Every write is absence-guarded — a user's own file is NEVER overwritten.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { SKILL_INSTALL_COMMAND } from '@wayai/core/constants';
import { resolveLayout, isDirectory } from './layout.js';
import { rootConfigPath, workspaceManifestPath } from './workspace-manifest.js';

/** One-line Claude Code shim that imports the sibling AGENTS.md. */
export const CLAUDE_MD_SHIM = '@AGENTS.md\n';

/**
 * Canonical repo-root `AGENTS.md` body. Mirrored verbatim in
 * `skills/wayai/references/agents-md-template.md` (drift-guarded). The install
 * command is interpolated so it can't drift from the rest of the codebase.
 *
 * ONE skill, ONE CLI. This file used to bootstrap two of each and instruct the
 * agent to load both skills every session — the shape
 * `docs/plans/rekor-as-wayai-platform-feature.md` §Invariant forbids ("one
 * always-on SKILL.md that loads both products"), because the second skill is
 * ~1k lines every chat-hub task pays for and never reads. The Data surface now
 * lives in the one skill's on-demand `references/bases/`.
 */
export const ROOT_AGENTS_MD = [
  '# Working with WayAI',
  '',
  'WayAI is the layer that talks to people across channels, plus the data layer',
  '(Bases) behind it. One CLI, one skill, one login.',
  '',
  '## At the start of every session',
  '',
  '1. Check for updates first, and update whatever is not current:',
  `   - Skill: \`wayai status\` → if newer, \`${SKILL_INSTALL_COMMAND}\``,
  '   - CLI: `wayai update`',
  '2. Load the WayAI skill at the start of every session — every time, even for a',
  '   quick task. It holds the real instructions for everything else.',
  '',
  'More detail if you need it: https://wayai.pro',
  '',
  '## When helping the person you are working with',
  '',
  '- They may be new to WayAI — use plain language, not jargon.',
  "- Don't explain things they didn't ask about. Only if they seem stuck or ask",
  '  what something means, give a one-line answer, then move on — don\'t turn',
  '  replies into tutorials.',
  '- Keep answers short and to the point.',
  '',
].join('\n');

/**
 * Scaffold for a hub folder's `AGENTS.md` — hub-specific memory the agent fills
 * in. `hubFolderName` titles it (e.g. "support--abc123").
 */
export function perHubAgentsMd(hubFolderName: string): string {
  return [
    `# ${hubFolderName}`,
    '',
    "Notes about THIS hub that the config files (hub.yaml, agents/) don't capture:",
    'why it exists, key decisions and the reasons behind them, business rules that',
    'only apply here, terminology, integration quirks.',
    '',
    'Keep it short and about *why / what is different* — platform mechanics live in',
    'the WayAI skill. Fill this in as you learn about the hub; delete this',
    'placeholder once you do.',
    '',
  ].join('\n');
}

/**
 * True when `gitRoot` is a WayAI workspace — the question this file's seeding
 * turns on, which is NOT "is it scoped to an org". A manifest carrying only
 * `default_hub` still makes the repo one of ours, and the bootstrap belongs
 * there; deferring to `readRepoConfig` instead would refuse to seed a repo mid
 * `wayai init` and turn a config question into a scaffolding one.
 *
 * Existence, deliberately, not content: a binding file too broken to parse is
 * still a WayAI repo, and the commands that need the org say so themselves.
 */
function isWorkspace(gitRoot: string): boolean {
  if (fs.existsSync(workspaceManifestPath(gitRoot))) return true;
  if (fs.existsSync(rootConfigPath(gitRoot))) return true;
  return isDirectory(resolveLayout(gitRoot).hubsDir);
}

/** Absence-guarded write. Returns the basename if written, null if it existed. */
export function writeIfAbsent(filePath: string, content: string): string | null {
  if (fs.existsSync(filePath)) return null;
  fs.writeFileSync(filePath, content, 'utf-8');
  return path.basename(filePath);
}

/**
 * Seed the repo-root `AGENTS.md` + `CLAUDE.md` bootstrap when they're missing.
 * No-op outside a WayAI workspace (so it never litters files into an unrelated
 * folder) and never overwrites an existing file. Returns the created basenames.
 */
export function ensureWorkspaceFiles(gitRoot: string): string[] {
  if (!isWorkspace(gitRoot)) return [];
  const created: string[] = [];
  const agents = writeIfAbsent(path.join(gitRoot, 'AGENTS.md'), ROOT_AGENTS_MD);
  if (agents) created.push(agents);
  const claude = writeIfAbsent(path.join(gitRoot, 'CLAUDE.md'), CLAUDE_MD_SHIM);
  if (claude) created.push(claude);
  return created;
}
